import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from "react";
import { DownOutlined } from "@ant-design/icons";
import { orgName, projectName } from "@app/config";
import { useCurrentUserUpdatedSubscription, useLogoutMutation, } from "@app/graphql";
import { Avatar, Col, Dropdown, Grid, Layout, Menu, message, Row, Space, Typography, } from "antd";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import headerLogo from "../assets/header-logo.png";
import { ErrorResult, H3, LocaleSelect, Redirect, StandardWidth, useIlmoContext, useIsMobile, useTranslation, Warn, } from ".";
const { Header, Content, Footer } = Layout;
const { Text } = Typography;
export { Link };
export const { useBreakpoint } = Grid;
export const contentMinHeight = "calc(100vh - 64px - 70px)";
export const contentMaxWidth = "75rem";
export var AuthRestrict;
(function (AuthRestrict) {
    AuthRestrict[AuthRestrict["NEVER"] = 0] = "NEVER";
    AuthRestrict[AuthRestrict["LOGGED_OUT"] = 1] = "LOGGED_OUT";
    AuthRestrict[AuthRestrict["LOGGED_IN"] = 2] = "LOGGED_IN";
    AuthRestrict[AuthRestrict["NOT_ADMIN"] = 4] = "NOT_ADMIN";
})(AuthRestrict || (AuthRestrict = {}));
export function SharedLayout({ title, titleHref, titleHrefAs, noPad = false, noHandleErrors = false, query, forbidWhen = AuthRestrict.NEVER, sider, children, displayFooter = true, }) {
    const router = useRouter();
    const currentUrl = router?.asPath;
    const [, logout] = useLogoutMutation();
    const { t } = useTranslation("common");
    const isMobile = useIsMobile();
    const context = useIlmoContext();
    const forbidsLoggedIn = forbidWhen & AuthRestrict.LOGGED_IN;
    const forbidsLoggedOut = forbidWhen & AuthRestrict.LOGGED_OUT;
    const forbidsNotAdmin = forbidWhen & AuthRestrict.NOT_ADMIN;
    const isSSR = typeof window === "undefined";
    const handleLogout = useCallback(() => {
        const reset = async () => {
            router.events.off("routeChangeComplete", reset);
            try {
                await logout();
                context.resetUrqlClient();
            }
            catch (e) {
                // Something went wrong; redirect to /logout to force logout.
                window.location.href = "/logout";
            }
        };
        router.events.on("routeChangeComplete", reset);
        router.push("/");
    }, [logout, context, router]);
    const renderChildren = (props) => {
        const { error, fetching, stale } = props;
        const inner = error && !fetching && !stale && !noHandleErrors ? (_jsx(ErrorResult, { error: error })) : typeof children === "function" ? (children(props)) : (children);
        if (data &&
            data.currentUser &&
            (forbidsLoggedIn || (forbidsNotAdmin && !data.currentUser.isAdmin))) {
            if (!isSSR && !noHandleErrors) {
                // Antd messages don't work with SSR
                message.error({
                    key: "access-denied",
                    content: `${t("error:accessDenied")}`,
                });
            }
            return _jsx(Redirect, { href: "/" });
        }
        else if (data?.currentUser === null &&
            !fetching &&
            !error &&
            forbidsLoggedOut) {
            return (_jsx(Redirect, { href: `/login?next=${encodeURIComponent(router.asPath)}` }));
        }
        return _jsx(StandardWidth, { noPad: noPad, children: inner });
    };
    const { data, fetching, error, stale } = query;
    /*
     * This will set up a GraphQL subscription monitoring for changes to the
     * current user. Interestingly we don't need to actually _do_ anything - no
     * rendering or similar - because the payload of this mutation will
     * automatically update Urql's cache which will cause the data to be
     * re-rendered wherever appropriate.
     */
    useCurrentUserUpdatedSubscription({
        // Skip checking for changes to the current user if
        // current user does not exist
        pause: !data?.currentUser,
    });
    return (_jsxs(Layout, { children: [_jsxs(Header, { style: {
                    boxShadow: "0 2px 8px #f0f1f2",
                    zIndex: 1,
                    overflow: "hidden",
                }, children: [_jsx(Head, { children: _jsx("title", { children: title ? `${title} — ${projectName}` : projectName }) }), _jsxs(Row, { wrap: false, children: [_jsx(Col, { sm: 8, style: { padding: "5px 0" }, xs: 12, children: _jsx(Link, { href: "/", children: _jsx("a", { children: _jsx(Image, { alt: "Prodeko", height: 50, placeholder: "blur", src: headerLogo, width: 50, priority: true }) }) }) }), !isMobile ? (_jsx(Col, { span: 8, children: _jsx(H3, { "data-cy": "layout-header-title", style: {
                                        margin: 0,
                                        padding: 0,
                                        textAlign: "center",
                                        lineHeight: "64px",
                                    }, children: titleHref ? (_jsx(Link, { as: titleHrefAs, href: titleHref, children: _jsx("a", { "data-cy": "layout-header-titlelink", children: title }) })) : (title) }) })) : null, _jsx(Col, { sm: 8, style: { textAlign: "right" }, xs: 12, children: _jsxs(Space, { size: "large", children: [_jsx(LocaleSelect, {}), data && data.currentUser ? (_jsx(Dropdown, { overlay: _jsxs(Menu, { children: [data.currentUser.isAdmin && (_jsx(Menu.Item, { children: _jsx(Link, { href: "/admin/event/list", children: _jsx("a", { "data-cy": "layout-link-admin", children: t("admin") }) }) }, "admin")), _jsx(Menu.Item, { children: _jsx(Link, { href: "/settings/profile", children: _jsx("a", { "data-cy": "layout-link-settings", children: _jsx(Warn, { okay: data.currentUser.isVerified, children: t("settings") }) }) }) }, "settings"), _jsx(Menu.Item, { children: _jsx("a", { onClick: handleLogout, children: t("logout") }) }, "logout")] }), trigger: ["click"], children: _jsxs("span", { "data-cy": "layout-dropdown-user", style: { whiteSpace: "nowrap", marginRight: 12 }, children: [_jsx(Avatar, { children: data?.currentUser?.name?.[0] || "?" }), _jsxs(Warn, { okay: data.currentUser.isVerified, children: [_jsx("span", { style: { marginLeft: 8, marginRight: 8 }, children: data.currentUser.name }), _jsx(DownOutlined, { style: { fontSize: 10, verticalAlign: "baseline" } })] })] }) })) : forbidsLoggedIn ? null : (_jsx(Link, { href: `/login?next=${encodeURIComponent(currentUrl)}`, children: _jsx("a", { "data-cy": "header-login-button", children: t("signin") }) }))] }) })] })] }), _jsxs(Layout, { hasSider: !!sider, children: [sider ? sider : null, _jsx(Content, { style: { minHeight: contentMinHeight }, children: renderChildren({
                            error,
                            stale,
                            fetching,
                            currentUser: data?.currentUser,
                        }) })] }), displayFooter && (_jsx(Footer, { children: _jsx(StandardWidth, { children: _jsxs(Row, { justify: "space-between", children: [_jsx(Col, { span: 12, children: _jsxs(Text, { children: ["\u00A9 ", new Date().getFullYear(), " ", orgName, " "] }) }), process.env.PRIVACY_URL && (_jsx(Col, { span: 12, style: { textAlign: "right" }, children: _jsx(Text, { children: _jsx("span", { children: _jsx("a", { href: process.env.PRIVACY_URL, style: { textDecoration: "underline" }, children: t("privacyPolicy") }) }) }) }))] }) }) }))] }));
}
//# sourceMappingURL=SharedLayout.js.map